import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  actionSetRecentActivitySets,
  actionSetRecentOperatorRuns,
  actionSetCurrentWorkOrderOp,
  actionOptimisticActivityUpdate,
  actionOptimisticActivitySetUpdate,
  actionOpenClockOutModal,
  actionCanonicalActivitySwitch,
  actionOptimisticActivitySwitch,
  actionCanonicalActivityUpdate,
  actionCanonicalActivitySetUpdate,
  actionSetLaborTickets,
  actionSetEmbeddedLaborTicket,
  actionSetWorkOrderOperationLaborTickets,
} from 'lib/actions';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getLatestActivityRef } from 'lib/selectors/getLatestActivityRef';
import { getMachine } from 'lib/selectors/getMachine';
import { getUnattendedOperatorPersonId } from 'lib/selectors/getUnattendedOperatorPersonId';
import { getClockOutFlowEnabled } from 'lib/selectors/getClockOutFlowEnabled';
import { getWorkOrderOperationLaborTickets } from 'lib/selectors/getWorkOrderOperationLaborTickets';
import { useRequestRecentActivitySets } from 'lib/hooks';
import useResyncMachineLaborTickets from 'lib/hooks/useResyncMachineLaborTickets';
import {
  throwCriticalErrorToast,
  throwSuccessToast,
  throwWarningToasts,
} from 'lib/utils/toast';

/**
 * Hook that handles labor ticket submit responses from embedded forms.
 * Encapsulates all the action dispatching logic for submit events.
 *
 * @param {Function} closeModal - Callback to close the modal (dispatches close action)
 * @returns {Function} handleSubmit - Function to call with submit event data
 */
const useHandleLaborTicketSubmit = (closeModal) => {
  const dispatch = useDispatch();
  const resyncLaborTickets = useResyncMachineLaborTickets();
  const { t: translate } = useTranslation();
  const { refetch: refetchRecentActivitySets } = useRequestRecentActivitySets();
  const latestActivitySet = useSelector(getLatestActivitySet);
  const machine = useSelector(getMachine);
  const latestActivityRef = useSelector(getLatestActivityRef);
  const unattendedOperatorPersonId = useSelector(getUnattendedOperatorPersonId);
  const currentOperator = useSelector(getCurrentOperator);
  const clockOutFlowEnabled = useSelector(getClockOutFlowEnabled);
  const workOrderOperationLaborTickets = useSelector(
    getWorkOrderOperationLaborTickets
  );

  const handleSubmit = useCallback(
    async (data) => {
      // Check for failure response (success: false indicates ERP/backend error)
      if (data.success === false) {
        const errorMessage =
          data.message || data.error || 'Submission failed. Please try again.';
        throwCriticalErrorToast(errorMessage);
        closeModal();
        return;
      }

      // Non-fatal ERP warnings (e.g. Epicor BPM InfoMessages): surface to the
      // operator without blocking the flow.
      throwWarningToasts(data.warnings);

      // Support both response structures: data.actionResults.* or data.*
      const actionData = data.actionResults || data;

      const {
        updateActivitySet,
        stopOperatorRun,
        pauseSetup,
        completeSetup,
        closeLaborTicket,
        createProductionTicket,
        createLaborTicket,
        startOperatorRun,
      } = actionData;

      // Capture the current operator BEFORE any state changes happen
      const operatorBeforeSubmit = currentOperator;
      if (closeLaborTicket?.personId === unattendedOperatorPersonId) {
        // Clear all tickets if unattended operator
        dispatch(actionSetLaborTickets([]));
        dispatch(actionSetEmbeddedLaborTicket(null));
      } else {
        // For submit-nats style responses (deferred processing / missing ref), resync from server
        // and do not trust payload-derived ticket data.
        const shouldResyncLaborTickets =
          Boolean(data?.processingDeferred) ||
          Boolean(
            createProductionTicket && !createProductionTicket.laborTicketRef
          ) ||
          Boolean(createLaborTicket && !createLaborTicket.laborTicketRef) ||
          Boolean(closeLaborTicket && !closeLaborTicket.laborTicketRef);
        if (shouldResyncLaborTickets) {
          try {
            await resyncLaborTickets();
          } catch {
            // Fall back to subscription reconciliation if immediate resync fails.
          }
        } else if (
          createProductionTicket ||
          createLaborTicket ||
          closeLaborTicket
        ) {
          const createdTicket = createProductionTicket || createLaborTicket;
          dispatch(actionSetLaborTickets([createdTicket || closeLaborTicket]));
          // Merge into operation tickets list for immediate selector consistency
          // (closedSetupAggMs, the material coverage baseline). A keep-open submit
          // returns both the closed ticket and its successor; both are merged so
          // the parts reported on the closed one count right away.
          const changed = [closeLaborTicket, createdTicket].filter((t) => {
            return Boolean(t?.laborTicketRef);
          });
          if (changed.length) {
            const changedRefs = new Set(
              changed.map((t) => {
                return t.laborTicketRef;
              })
            );
            const merged = (workOrderOperationLaborTickets || []).filter(
              (t) => {
                return !changedRefs.has(t.laborTicketRef);
              }
            );
            merged.push(...changed);
            dispatch(actionSetWorkOrderOperationLaborTickets(merged));
          }
        }
      }

      if (updateActivitySet) {
        dispatch(actionSetRecentActivitySets([updateActivitySet.activitySet]));
        if (updateActivitySet.activitySet.closedAt) {
          dispatch(actionSetCurrentWorkOrderOp(null));
        }
      }

      if (startOperatorRun) {
        const { operator, machine: runMachine, ...rest } = startOperatorRun;
        dispatch(
          actionSetRecentOperatorRuns({
            operatorRuns: [
              {
                ...rest,
                operatorId: operator.id,
                machineId: runMachine.id,
              },
            ],
          })
        );
      }

      if (stopOperatorRun) {
        const { operator, ...rest } = stopOperatorRun;
        dispatch(
          actionSetRecentOperatorRuns({
            operatorRuns: [
              { ...rest, operatorId: operator.id, machineId: machine.id },
            ],
          })
        );
      }

      if (pauseSetup) {
        // Update the individual activity with the new endAt time
        dispatch(
          actionOptimisticActivityUpdate({
            activity: {
              activityRef: pauseSetup.activityRef,
              endAt: pauseSetup.endAt,
              end: pauseSetup.endAt,
              activitySetRef: latestActivitySet?.activitySetRef,
            },
          })
        );
        dispatch(
          actionCanonicalActivityUpdate({
            activity: {
              ...pauseSetup,
              end: pauseSetup.endAt,
            },
            activityRef: pauseSetup.activityRef,
          })
        );
        dispatch(
          actionOptimisticActivitySetUpdate({
            activitySet: {
              ...latestActivitySet,
              updatedAt: pauseSetup.endAt,
            },
          })
        );
        dispatch(
          actionCanonicalActivitySetUpdate({
            activitySet: {
              ...latestActivitySet,
              updatedAt: pauseSetup.endAt,
            },
            activitySetRef: latestActivitySet?.activitySetRef,
          })
        );
      }

      if (completeSetup) {
        // Update the individual activity with the full response from the API
        dispatch(
          actionOptimisticActivitySwitch({
            optimisticActivity: {
              ...completeSetup,
              start: completeSetup.startAt,
              end: completeSetup.endAt,
            },
            latestActivityRef,
          })
        );

        dispatch(
          actionCanonicalActivitySwitch({
            activity: {
              ...completeSetup,
              start: completeSetup.startAt,
              end: completeSetup.endAt,
            },
            activityRef: completeSetup.activityRef,
            optimisticAcitivityRef: null,
          })
        );
      }

      if (
        clockOutFlowEnabled &&
        stopOperatorRun &&
        stopOperatorRun.machine.id === machine.id
      ) {
        // Regular sign out. The personId the clock-out endpoint keys on is NOT on
        // stopOperatorRun.operator (only {id, name}) nor stopOperatorRun.timesheet
        // (nulled out here) — so pass the erpId of the operator captured before the
        // submit, falling back to the closed ticket's personId. Without this the
        // modal opens with no personId and can't take the single-ticket fast path.
        const clockOutPersonId =
          operatorBeforeSubmit?.erpId || closeLaborTicket?.personId;
        dispatch(actionOpenClockOutModal(clockOutPersonId));
      }

      if (
        clockOutFlowEnabled &&
        startOperatorRun &&
        startOperatorRun.machineId === machine.id
      ) {
        // Unattended operator starting - use the operator from before submit
        // operatorBeforeSubmit should be the actual operator who submitted the form
        dispatch(actionOpenClockOutModal(operatorBeforeSubmit?.erpId));
      }

      throwSuccessToast(translate('Work submitted'));

      // Close the modal and clean up
      closeModal();

      // Refetch activity sets to ensure state is in sync after embedded form submit
      refetchRecentActivitySets();
    },
    [
      dispatch,
      currentOperator,
      unattendedOperatorPersonId,
      latestActivitySet,
      machine,
      latestActivityRef,
      closeModal,
      refetchRecentActivitySets,
      clockOutFlowEnabled,
      resyncLaborTickets,
      workOrderOperationLaborTickets,
      translate,
    ]
  );

  return handleSubmit;
};

export default useHandleLaborTicketSubmit;
